import { Sounds } from '../../../@resources/sounds';
import { debounce } from '../../../CodesThatsGottaGoSomewhereElse';
import { Component, ObjectManager } from '../../../concepts/objects';
import { AudioManager } from '../../../platform/AudioManager';
import { Point } from '../../../platform/geom/Point';
import { ConfettiCannon } from '../../canvas/ConfettiCannon';
import { RelativeTo } from '../../canvas/entities/Physical';
import { ActiveObject, resolve } from '../../Container';
import { Stopwatch, StopwatchReadingAttr } from './Stopwatch';

const formatDigit = (n: number) => n.toString().padStart(2, '0');

export class StopwatchMechanics extends ActiveObject {

    @resolve(AudioManager) private readonly audio!: AudioManager;
    @resolve(ConfettiCannon) private readonly confetti!: ConfettiCannon;
    @resolve(ObjectManager) private readonly objects!: ObjectManager;

    private skipSync = false;
    private timerId = null as any;

    public start() {
        const syncDebounced = debounce(this.sync, 0);
        const scheduleSync = () => {
            if (this.skipSync) return;
            syncDebounced();
        };
        this.burden(
            this.objects.on('Stopwatch', 'allocated', scheduleSync),
            this.objects.on('Stopwatch', 'released', scheduleSync),
            this.objects.on('Stopwatch', 'released', this.cancelDeletedAlarm),
            this.objects.createTrigger('StopwatchMechanics.ResetOnChange')
                .for(Stopwatch).onChangesTo('minutes' as any, 'seconds' as any).doForEach(this.resetInstance),
            this.objects.createTrigger('StopwatchMechanics.ForceTick')
                .for(Stopwatch).onChangesTo('timeStarted', 'minutes', 'seconds').do(this.tickGlobalTimer),
        );
        this.sync();
    }

    private sync = () => {
        if (this.skipSync) return;
        const watches = this.objects.getAll(Stopwatch);
        if (watches.size) {
            this.startGlobalTimer();
        }
        else {
            this.stopGlobalTimer();
        }
    };

    private resetInstance = (watch: Stopwatch) => {
        if (watch.timeStarted) {
            watch.timeStarted = (new Date()).getTime();
        }
    };

    private updateInstanceReading = (watch: Stopwatch) => {
        if (!watch.timeStarted) {
            StopwatchReadingAttr.set(watch, null);
        }
        else {
            const [mi, si] = watch.getTimeRemaining();
            const [nm, ns] = [formatDigit(mi), formatDigit(si)];

            const prev = (StopwatchReadingAttr.get(watch) ?? ['00', '00']).join('');
            const next = [nm, ns].join('');

            // Is it time for the timer?
            if (next === '0005' && prev !== next) {
                this.audio.play(`${watch.id}/alarm`, Sounds.Stopwatch, .20);
            }

            // Did we just finish?
            if (next === '0000' && prev !== next) {
                const wb = watch.bounds(RelativeTo.Model);
                const at = new Point(wb.center().x, wb.top);
                this.confetti.ignite([at, 1]);
            }

            StopwatchReadingAttr.set(watch, [nm, ns]);
        }
    };

    private cancelDeletedAlarm = ([watch]: [Component]) => {
        this.audio.stop(`${watch.id}/alarm`);
    };

    private startGlobalTimer = () => {
        if (this.timerId) return;
        this.timerId = setInterval(this.tickGlobalTimer, 1000);
    };

    private stopGlobalTimer = () => {
        clearInterval(this.timerId);
        this.timerId = null;
    };

    private tickGlobalTimer = () => {
        try {
            this.skipSync = true;
            this.objects.getAll(Stopwatch).forEach(x => this.updateInstanceReading(x));
        }
        finally {
            this.skipSync = false;
        }
    };
}
